import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

import { SoftwareUpdate } from '../../models/software-update.model';
import { AssignableDevice, AssignableDevicesData, TenantAssignableDevices } from '../../models/assignable-device.model';
import { BatchAssignmentData } from '../../models/batch-assignment.model';
import { SoftwareUpdateService } from '../../services/software-update.service';
import { Tenant } from '../../../admin/tenants/models/tenant.model';
import { TenantService } from '../../../admin/tenants/services/tenant.service';
import { Icon } from '../../../../shared/components/icon/icon';
import { Grid } from '../../../../shared/components/grid/grid';
import { GridCellDirective } from '../../../../shared/components/grid/directives/grid-cell.directive';
import { GridColumn, GridSort, gridColumns } from '../../../../shared/components/grid/models/grid.model';
import { ToastService } from '../../../../shared/services/toast-service';
import { LabelBadge } from '../../../../shared/components/label-badge/label-badge';
import { tenantAccent, tenantHue } from '../../../../shared/utils/tenant-color';
import { assignmentGridStatus } from '../../utils/assignment-status.util';
import { ConfirmAssignmentDialogComponent, ConfirmTenantGroup } from './confirm-assignment-dialog.component';

/** A selected device, remembered with enough context to survive re-filtering. */
interface SelectedDevice {
  clientId: string;
  tenantId: string;
  tenantName: string;
}

/** Fetch page size per tenant group. Bumped when "Select all eligible" runs
 *  a scoped, larger fetch to resolve every matching id — see selectAllEligible(). */
const PAGE_SIZE = 20;
const SELECT_ALL_PAGE_SIZE = 500;

/**
 * Assign devices tab (handoff screen 4b) — cross-tenant device search and
 * selection, grouped by tenant, feeding the (not-yet-built) confirm step.
 *
 * Data comes from `SoftwareUpdateService.getAssignableDevices()`
 * (`GET .../assignable-devices`, contract per
 * assignable-devices-api-handoff.md). Two deliberate scope cuts versus the
 * original wireframe copy (confirmed with backend, not gaps to fix):
 * - search matches clientId/serialNumber only, not name/location
 * - there's no "Model / location" column — we don't have that data on a device
 *
 * "Continue to confirm" opens ConfirmAssignmentDialogComponent, which submits
 * the real batch via `SoftwareUpdateService.assignBatch()`. "Select all
 * eligible" still resolves client-side (see its own doc comment) since the
 * batch endpoint takes an explicit clientId list, not a filter descriptor.
 */
@Component({
  selector: 'app-assign-devices-tab',
  standalone: true,
  imports: [FormsModule, Icon, Grid, GridCellDirective, ConfirmAssignmentDialogComponent, LabelBadge],
  templateUrl: './assign-devices-tab.component.html',
  // See software-update-detail.component.ts for why: without this, the host
  // custom element sizes to its content instead of stretching to fill the
  // parent's flex column, so the sticky footer's `mt-auto` has no room to
  // push down to the page bottom.
  host: { class: 'contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignDevicesTabComponent implements OnInit, OnDestroy {
  readonly update = input.required<SoftwareUpdate>();
  /** Emitted once a batch is successfully submitted, so the parent can jump
   *  to the Rollout tab and refresh its assigned-count badge. */
  readonly assigned = output<void>();

  private readonly service = inject(SoftwareUpdateService);
  private readonly tenantService = inject(TenantService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly searchInput$ = new Subject<string>();

  // ── Filters ────────────────────────────────────────────────────────────
  query = '';
  currentVersion = '';
  readonly tenantFilter = signal<Set<string>>(new Set());
  readonly hideAssigned = signal(true);
  readonly tenantMenuOpen = signal(false);
  readonly allTenants = signal<Tenant[]>([]);

  // ── Data / UI state ───────────────────────────────────────────────────
  readonly data = signal<AssignableDevicesData | null>(null);
  readonly isLoading = signal(false);
  readonly isSelectingAll = signal(false);
  readonly expandedTenants = signal<Set<string>>(new Set());
  /** Keyed by clientId; survives filter/search/pagination changes. */
  readonly selected = signal<Map<string, SelectedDevice>>(new Map());

  /** Per-tenant device list — the shared app-grid, same component used in
   *  the Software Updates list and the Rollout tab. Its checkbox column has
   *  no per-row "disabled" hook, so an already-assigned device can still be
   *  clicked when "Hide already assigned" is off; onTenantSelectionChange()
   *  filters those back out immediately (a one-frame flicker, not a real
   *  gap — the batch endpoint would reject them anyway). */
  readonly assignRowId = (row: AssignableDevice) => row.clientId;
  /** Narrows the erased-generic row that projected grid templates receive. */
  readonly asAssignableDevice = (row: unknown): AssignableDevice => row as AssignableDevice;

  readonly deviceColumns: GridColumn<AssignableDevice>[] = gridColumns<AssignableDevice>([
    { key: 'clientId', label: 'Device ID', type: 'template', alwaysVisible: true, width: 150 },
    { key: 'serialNumber', label: 'Serial', type: 'text', width: 150, value: (row) => row.serialNumber ?? '—' },
    {
      key: 'currentFirmwareVersion',
      label: 'Current version',
      type: 'text',
      width: 130,
      value: (row) => row.currentFirmwareVersion ?? '—',
    },
    {
      key: 'assignmentStatus',
      label: 'Assignment status',
      type: 'status',
      width: 200,
      value: (row) => row.assignmentStatus,
      status: (_, row) => assignmentGridStatus(row.assignmentStatus, row.failedReason),
    },
  ]);

  // The device table has nothing meaningful to sort by (row order is
  // whatever the backend returns) — no columns are marked sortable, so this
  // should never actually fire.
  onSortChange(_sort: GridSort): void {}

  /** True once the user has narrowed the tenant filter below "all" — the
   *  default state (every tenant selected) reads as unfiltered in the UI. */
  readonly isTenantFilterNarrowed = computed(
    () => this.tenantFilter().size > 0 && this.tenantFilter().size < this.allTenants().length,
  );

  readonly selectedCount = computed(() => this.selected().size);

  /** Full per-tenant clientId lists — the source both the footer's per-tenant
   *  pills and the confirm dialog / batch request derive their view from. */
  readonly confirmGroups = computed<ConfirmTenantGroup[]>(() => {
    const byTenant = new Map<string, ConfirmTenantGroup>();
    for (const d of this.selected().values()) {
      const group = byTenant.get(d.tenantId) ?? { tenantId: d.tenantId, tenantName: d.tenantName, clientIds: [] };
      group.clientIds.push(d.clientId);
      byTenant.set(d.tenantId, group);
    }
    return [...byTenant.values()];
  });

  readonly selectedByTenant = computed(() =>
    this.confirmGroups().map((g) => ({ tenantId: g.tenantId, name: g.tenantName, count: g.clientIds.length })),
  );

  readonly isConfirmOpen = signal(false);

  ngOnInit(): void {
    this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fetch());

    // The API treats an empty/omitted tenantIds as "match nothing" (per the
    // handoff), not "match everything" — so the device fetch can't start
    // until every tenant is known and pre-selected, or the toolbar would
    // default to showing zero devices.
    this.tenantService.getTenants(0, 200).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.allTenants.set(res.data);
        this.tenantFilter.set(new Set(res.data.map((t) => t.id)));
        this.fetch();
      },
      error: () => this.toastService.show('Failed to load tenants', 'error'),
    });
  }

  ngOnDestroy(): void {
    // searchInput$ is a plain Subject (not an Observable input); complete it
    // explicitly since takeUntilDestroyed only tears down the subscription,
    // not the subject itself.
    this.searchInput$.complete();
  }

  onQueryInput(value: string): void {
    this.query = value;
    this.searchInput$.next(value);
  }

  onVersionInput(value: string): void {
    this.currentVersion = value;
    this.fetch();
  }

  toggleTenantMenu(): void {
    this.tenantMenuOpen.update((v) => !v);
  }

  toggleTenantFilter(tenantId: string): void {
    const next = new Set(this.tenantFilter());
    next.has(tenantId) ? next.delete(tenantId) : next.add(tenantId);
    this.tenantFilter.set(next);
    this.fetch();
  }

  toggleHideAssigned(): void {
    this.hideAssigned.update((v) => !v);
    this.fetch();
  }

  private fetch(): void {
    this.isLoading.set(true);
    this.service
      .getAssignableDevices(this.update().id, {
        q: this.query || undefined,
        tenantIds: this.tenantFilter().size ? [...this.tenantFilter()] : undefined,
        currentVersion: this.currentVersion || undefined,
        hideAssigned: this.hideAssigned(),
        page: 0,
        size: PAGE_SIZE,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.data.set(res.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show('Failed to load devices', 'error');
          this.isLoading.set(false);
        },
      });
  }

  // ── Group expand/collapse ────────────────────────────────────────────
  isExpanded(tenantId: string): boolean {
    return this.expandedTenants().has(tenantId);
  }

  toggleGroup(tenantId: string): void {
    const next = new Set(this.expandedTenants());
    next.has(tenantId) ? next.delete(tenantId) : next.add(tenantId);
    this.expandedTenants.set(next);
  }

  expandAll(): void {
    this.expandedTenants.set(new Set((this.data()?.tenants ?? []).map((t) => t.tenantId)));
  }

  collapseAll(): void {
    this.expandedTenants.set(new Set());
  }

  /**
   * Fetches one tenant's page of the grouped table — the API paginates per
   * tenant group, so this re-requests scoped to a single tenant and
   * *replaces* that group's rows (real pagination via app-grid, unlike the
   * old "Show more" append-only link this superseded).
   */
  onTenantPageChange(group: TenantAssignableDevices, page: number): void {
    this.service
      .getAssignableDevices(this.update().id, {
        q: this.query || undefined,
        tenantIds: [group.tenantId],
        currentVersion: this.currentVersion || undefined,
        hideAssigned: this.hideAssigned(),
        page,
        size: PAGE_SIZE,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const updated = res.data.tenants.find((t) => t.tenantId === group.tenantId);
          if (!updated) return;
          this.data.update((d) => {
            if (!d) return d;
            return {
              ...d,
              tenants: d.tenants.map((t) => (t.tenantId === group.tenantId ? updated : t)),
            };
          });
        },
        error: () => this.toastService.show('Failed to load devices', 'error'),
      });
  }

  /** Total row count for this tenant's grid pagination — matches whichever
   *  count group.totalPages was itself computed against (see the handoff). */
  tenantTotalElements(group: TenantAssignableDevices): number {
    return this.hideAssigned() ? group.notAssignedCount : group.totalCount;
  }

  // ── Selection ─────────────────────────────────────────────────────────
  isSelected(clientId: string): boolean {
    return this.selected().has(clientId);
  }

  /** The current selection, scoped to one tenant — a one-way view for the
   *  per-tenant grid's `selectedIds`. */
  tenantSelectedIds(group: TenantAssignableDevices): string[] {
    const ids: string[] = [];
    for (const d of this.selected().values()) {
      if (d.tenantId === group.tenantId) ids.push(d.clientId);
    }
    return ids;
  }

  /**
   * Reconciles the grid's selection against the Map that's the actual
   * source of truth, for one tenant's currently-loaded page. Filters out
   * any id whose device isn't `not_assigned` — the grid's checkbox column
   * has no per-row disable, so this is what actually enforces "already-
   * assigned devices are never selectable" when the hide-assigned toggle is
   * off (when it's on, every row is already not_assigned, so this is a
   * no-op filter).
   */
  onTenantSelectionChange(group: TenantAssignableDevices, ids: readonly string[]): void {
    const requested = new Set(ids);
    const next = new Map(this.selected());
    for (const device of group.devices) {
      const wants = requested.has(device.clientId);
      const eligible = device.assignmentStatus === 'not_assigned';
      if (wants && eligible) {
        next.set(device.clientId, { clientId: device.clientId, tenantId: group.tenantId, tenantName: group.tenantName });
      } else {
        next.delete(device.clientId);
      }
    }
    this.selected.set(next);
  }

  /** Tri-state, scoped to currently-loaded rows only — see the open question
   *  in the rollout plan about "select all matching filters" vs "loaded rows". */
  groupState(group: TenantAssignableDevices): 'none' | 'some' | 'all' {
    const selectable = group.devices.filter((d) => d.assignmentStatus === 'not_assigned');
    if (!selectable.length) return 'none';
    const selectedCount = selectable.filter((d) => this.isSelected(d.clientId)).length;
    if (selectedCount === 0) return 'none';
    return selectedCount === selectable.length ? 'all' : 'some';
  }

  toggleGroupSelection(group: TenantAssignableDevices): void {
    const state = this.groupState(group);
    const next = new Map(this.selected());
    const selectable = group.devices.filter((d) => d.assignmentStatus === 'not_assigned');
    if (state === 'all') {
      selectable.forEach((d) => next.delete(d.clientId));
    } else {
      selectable.forEach((d) =>
        next.set(d.clientId, { clientId: d.clientId, tenantId: group.tenantId, tenantName: group.tenantName }),
      );
    }
    this.selected.set(next);
  }

  /**
   * Selects every device matching the current filters — not just loaded
   * rows. There's no server-side "select by filter" yet (that's endpoint #2,
   * still open with backend), so this pages through every tenant at a large
   * page size to resolve the full id list client-side. Fine for hundreds of
   * devices; will need revisiting if the fleet grows much larger.
   */
  selectAllEligible(): void {
    const d = this.data();
    if (!d) return;
    this.isSelectingAll.set(true);
    this.service
      .getAssignableDevices(this.update().id, {
        q: this.query || undefined,
        tenantIds: this.tenantFilter().size ? [...this.tenantFilter()] : undefined,
        currentVersion: this.currentVersion || undefined,
        hideAssigned: true, // "eligible" always excludes already-assigned devices
        page: 0,
        size: SELECT_ALL_PAGE_SIZE,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const next = new Map(this.selected());
          for (const group of res.data.tenants) {
            for (const device of group.devices) {
              if (device.assignmentStatus !== 'not_assigned') continue;
              next.set(device.clientId, {
                clientId: device.clientId,
                tenantId: group.tenantId,
                tenantName: group.tenantName,
              });
            }
          }
          this.selected.set(next);
          this.isSelectingAll.set(false);
          if (res.data.tenants.some((t) => t.totalPages > 1)) {
            this.toastService.show(
              'Some tenants had more eligible devices than could be loaded in one pass — selection may be incomplete.',
              'error',
            );
          }
        },
        error: () => {
          this.toastService.show('Failed to select all eligible devices', 'error');
          this.isSelectingAll.set(false);
        },
      });
  }

  clearSelection(): void {
    this.selected.set(new Map());
  }

  /** Abandons the in-progress selection by leaving the page entirely — the
   *  component is destroyed on navigation, so there's nothing to clear
   *  first. Distinct from "Clear", which stays on this page. */
  cancel(): void {
    this.router.navigate(['/admin/software-updates']);
  }

  continueToConfirm(): void {
    this.isConfirmOpen.set(true);
  }

  closeConfirm(): void {
    this.isConfirmOpen.set(false);
  }

  onBatchAssigned(result: BatchAssignmentData): void {
    this.isConfirmOpen.set(false);
    // Drop only the devices that actually succeeded — failed ones (per
    // resultsByTenant) stay selected so the admin can see and retry them
    // rather than losing the selection on a partial failure.
    const failedIds = new Set(
      Object.values(result.resultsByTenant)
        .flat()
        .filter((r) => !r.assigned)
        .map((r) => r.clientId),
    );
    this.selected.update((current) => {
      const next = new Map(current);
      for (const clientId of current.keys()) {
        if (!failedIds.has(clientId)) next.delete(clientId);
      }
      return next;
    });
    this.fetch(); // refresh assignment statuses now that some devices succeeded
    if (result.assigned > 0) this.assigned.emit();
  }

  // ── Display helpers ──────────────────────────────────────────────────
  tenantDot(tenantId: string): string {
    return tenantAccent(tenantId).dot;
  }

  /** Hue for the tenant identity pill — same deterministic mapping as tenantDot(). */
  tenantHueOf(tenantId: string) {
    return tenantHue(tenantId);
  }

}
